import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { PageHeader, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { MessageSquare } from "lucide-react";
import { Plus, Search, Eye, Download, FileText, FileSpreadsheet, Upload, FileUp, ChevronUp, ChevronDown, ArrowUpDown, Pencil } from "lucide-react";
import { useBranch } from "../context/BranchContext";
import { MergeCustomersDialog } from "../components/MergeCustomersDialog";
import { useAuth } from "../context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const EMPTY = { name: "", mobile: "", whatsapp: "", email: "", gender: "", dob: "", address: "", notes: "" };
const MARKETING_SOURCES = ["Walk-in", "Pamphlet", "Facebook / Instagram", "Google", "Existing Client Referral", "Staff Referral", "Friend / Family", "Website / Online Booking", "Other"];

export default function Customers() {
  const { branchParam, branches } = useBranch();
  const { user } = useAuth();
  const isAdmin = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const [importOpen, setImportOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [marketing, setMarketing] = useState(null);
  useEffect(() => {
    if (!detail?.id) { setMarketing(null); return; }
    api.get(`/campaigns/customer/${detail.id}/history`).then((r) => setMarketing(r.data)).catch(() => setMarketing(null));
  }, [detail?.id]);
  const toggleConsent = async (val) => {
    if (!detail) return;
    try {
      await api.post(`/campaigns/customer/${detail.id}/consent`, { opted_in: val });
      setMarketing((m) => ({ ...(m || {}), marketing_opted_in: val }));
      toast.success(val ? "Marketing: Opted In" : "Marketing: Opted Out");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [totalClients, setTotalClients] = useState(null);
  const [insights, setInsights] = useState(null);

  const load = () => api.get("/customers", { params: { q: q || undefined, sort, branch_id: branchParam || undefined } }).then((r) => setCustomers(r.data));
  const loadCount = () => api.get("/customers/count", { params: { branch_id: branchParam || undefined } }).then((r) => setTotalClients(r.data.total_clients)).catch(() => {});
  const loadInsights = () => api.get("/customers/insights", { params: { branch_id: branchParam || undefined } }).then((r) => setInsights(r.data)).catch(() => {});
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, sort, branchParam]);
  useEffect(() => { loadCount(); loadInsights(); }, [branchParam]);

  const exportFile = async (fmt) => {
    try {
      const res = await api.get(`/customers/export.${fmt}`, { params: { branch_id: branchParam || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "pdf" ? "define_salons_customers.pdf" : "define_salons_customers.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) { toast.error("Export failed"); }
  };

  const save = async () => {
    if (!form.name?.trim() || !form.mobile?.trim()) { toast.error("Name and mobile are required"); return; }
    try {
      if (editId) {
        await api.put(`/customers/${editId}`, form);
        toast.success("Changes saved");
        if (detail?.id === editId) { const { data } = await api.get(`/customers/${editId}`); setDetail(data); }
      } else {
        await api.post("/customers", form);
        toast.success("Customer saved");
      }
      setOpen(false); setEditId(null); load(); loadCount(); loadInsights();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const view = async (id) => { const { data } = await api.get(`/customers/${id}`); setDetail(data); };
  const setSource = async (val) => {
    try {
      await api.patch(`/customers/${detail.id}/marketing-source`, { marketing_source: val });
      setDetail({ ...detail, marketing_source: val });
      toast.success("Marketing source updated");
      loadInsights();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openAdd = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = () => {
    if (!detail) return;
    setForm({
      name: detail.name ?? "", mobile: detail.mobile ?? "", whatsapp: detail.whatsapp ?? "", email: detail.email ?? "",
      gender: detail.gender ?? "", dob: detail.dob ?? "", address: detail.address ?? "", notes: detail.notes ?? "",
      preferred_branch: detail.preferred_branch ?? null, preferred_staff: detail.preferred_staff ?? null,
      marketing_consent: detail.marketing_consent ?? true, whatsapp_optin: detail.whatsapp_optin ?? true, membership_id: detail.membership_id ?? null,
    });
    setEditId(detail.id); setOpen(true);
  };

  const SORT_COLS = {
    visits: { desc: "visits_high", asc: "visits_low" },
    spending: { desc: "spend_high", asc: "spend_low" },
    last: { desc: "recent_visit", asc: "oldest_visit" },
  };
  const colDir = (col) => (sort === SORT_COLS[col].desc ? "desc" : sort === SORT_COLS[col].asc ? "asc" : null);
  const toggleCol = (col) => setSort((s) => (s === SORT_COLS[col].desc ? SORT_COLS[col].asc : SORT_COLS[col].desc));
  const SortTH = ({ col, label, right }) => {
    const dir = colDir(col);
    return (
      <TableHead className={right ? "text-right" : ""}>
        <button type="button" onClick={() => toggleCol(col)} data-testid={`sort-${col}`}
          className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${right ? "flex-row-reverse" : ""} ${dir ? "text-primary" : ""}`}>
          {label}
          {dir === "desc" ? <ChevronDown size={13} /> : dir === "asc" ? <ChevronUp size={13} /> : <ArrowUpDown size={12} className="opacity-40" />}
        </button>
      </TableHead>
    );
  };

  return (
    <div>
      <PageHeader title="Customer CRM" subtitle="Profiles, visits & spending"
        actions={
          <div className="flex items-center gap-2">
            {isAdmin && <MergeCustomersDialog onDone={() => { load(); loadCount(); loadInsights(); }} />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-sm border-border" data-testid="export-customers-btn"><Download size={16} className="mr-1" /> Export</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportFile("pdf")} data-testid="export-pdf"><FileText size={15} className="mr-2" /> Export PDF</DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportFile("xlsx")} data-testid="export-excel"><FileSpreadsheet size={15} className="mr-2" /> Export Excel</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {isAdmin && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-customers-btn" className="rounded-sm border-border"><Upload size={16} className="mr-1" /> Import</Button>}
            <Button onClick={openAdd} data-testid="add-customer-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Customer</Button>
          </div>
        } />

      {insights && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5" data-testid="crm-insights">
          <Card className="p-4 bg-card border-border">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">New vs Returning</p>
            <div className="flex items-end justify-between gap-4">
              <div><p className="font-display text-2xl" data-testid="insight-new">{insights.new}</p><p className="text-xs text-muted-foreground">New ({insights.new_pct}%)</p></div>
              <div className="text-right"><p className="font-display text-2xl text-primary" data-testid="insight-returning">{insights.returning}</p><p className="text-xs text-muted-foreground">Returning ({insights.returning_pct}%)</p></div>
            </div>
            <div className="mt-3 h-2 w-full rounded-full bg-secondary overflow-hidden flex">
              <div className="h-full bg-muted-foreground/50" style={{ width: `${insights.new_pct}%` }} />
              <div className="h-full bg-primary" style={{ width: `${insights.returning_pct}%` }} />
            </div>
          </Card>
          <Card className="p-4 bg-card border-border lg:col-span-2" data-testid="insight-top-spenders">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Top Spenders {branchParam ? "(this branch)" : "(all branches)"}</p>
            {insights.top_spenders?.length ? (
              <div className="space-y-1.5">
                {insights.top_spenders.map((t, i) => (
                  <div key={t.mobile + i} className="flex items-center justify-between text-sm border-b border-border/40 pb-1.5 last:border-0">
                    <span className="flex items-center gap-2"><span className="text-xs text-muted-foreground w-4">#{i + 1}</span>{t.name} <span className="text-xs text-muted-foreground">· {t.visits} visit(s)</span></span>
                    <span className="font-medium text-primary">{rupee(t.spend)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No spending data yet.</p>}
          </Card>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
          <Input placeholder="Search by name or mobile…" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="customer-search" className="pl-9 bg-secondary" />
        </div>
        <div data-testid="total-clients" className="flex items-center gap-2 px-4 py-2 rounded-sm bg-secondary border border-border">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Total Clients</span>
          <span className="font-display text-xl text-primary" data-testid="total-clients-count">{totalClients ?? "—"}</span>
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="bg-secondary w-52" data-testid="customer-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="spend_high">Highest Spending</SelectItem>
            <SelectItem value="spend_low">Lowest Spending</SelectItem>
            <SelectItem value="visits_high">Most Visits</SelectItem>
            <SelectItem value="visits_low">Least Visits</SelectItem>
            <SelectItem value="recent_visit">Recent Visit</SelectItem>
            <SelectItem value="oldest_visit">Oldest Visit</SelectItem>
            <SelectItem value="total_clients">Total Clients</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Name</TableHead><TableHead>Mobile</TableHead>
              <SortTH col="visits" label="Visits" right />
              <SortTH col="spending" label="Spending" right />
              <SortTH col="last" label="Last Visit" />
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.map((c) => (
              <TableRow key={c.id} className="border-border" data-testid={`customer-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.mobile}</TableCell>
                <TableCell className="text-right">{c.total_visits || 0}</TableCell>
                <TableCell className="text-right text-primary">{rupee(c.total_spending)}</TableCell>
                <TableCell className="text-muted-foreground">{c.last_visit || "—"}</TableCell>
                <TableCell><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => view(c.id)} data-testid={`view-customer-${c.id}`}><Eye size={15} /></Button></TableCell>
              </TableRow>
            ))}
            {!customers.length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No customers.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit Customer" : "Add New Customer"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Full Name *</Label><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Mobile Number *</Label><Input data-testid="customer-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Gender</Label>
                <Select value={form.gender || ""} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="customer-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{["Male", "Female", "Other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Date of Birth</Label><Input type="date" data-testid="customer-dob" value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div><Label className="text-xs">Email</Label><Input data-testid="customer-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-secondary mt-1" /></div>
            <div><Label className="text-xs">Address</Label><Input data-testid="customer-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-secondary mt-1" /></div>
            <div><Label className="text-xs">Notes</Label><Input data-testid="customer-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-secondary mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="customer-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">{editId ? "Save Changes" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex-row items-center justify-between space-y-0 pr-8">
            <DialogTitle className="font-display text-2xl">{detail?.name}</DialogTitle>
            {detail && <Button size="sm" variant="outline" className="h-8 rounded-sm border-primary/50 text-primary" onClick={openEdit} data-testid="crm-edit-customer"><Pencil size={13} className="mr-1" /> Edit</Button>}
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-3 bg-secondary border-border" data-testid="cust-wallet-balance"><p className="text-xs text-muted-foreground">Global Wallet</p><p className="font-display text-2xl text-primary">{rupee(detail.wallet_balance)}</p></Card>
                <Card className="p-3 bg-secondary border-border"><p className="text-xs text-muted-foreground">Cashback</p><p className="font-display text-2xl">{rupee(detail.cashback_balance)}</p></Card>
                <Card className="p-3 bg-secondary border-border"><p className="text-xs text-muted-foreground">Total Visits</p><p className="font-display text-2xl">{detail.total_visits || 0}</p></Card>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                <div>Mobile: <span className="text-foreground font-mono">{detail.mobile}</span></div>
                <div>Total Spending: <span className="text-foreground">{rupee(detail.total_spending)}</span></div>
              </div>
              <div className="flex items-center gap-2 flex-wrap" data-testid="cust-marketing-source">
                <span className="text-xs text-muted-foreground">Marketing Source:</span>
                {isAdmin ? (
                  <Select value={detail.marketing_source || ""} onValueChange={setSource}>
                    <SelectTrigger className="h-8 w-56 bg-secondary" data-testid="cust-source-select"><SelectValue placeholder="Not recorded" /></SelectTrigger>
                    <SelectContent>{MARKETING_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <span className="text-foreground" data-testid="cust-source-value">{detail.marketing_source || "Not recorded"}</span>}
              </div>
              {detail.branch_visits?.length > 0 && (
                <div data-testid="cust-branch-visits">
                  <p className="font-medium mb-2">Visits by Branch (one central profile)</p>
                  {detail.branch_visits.map((b) => (
                    <div key={b.branch_id} className="flex justify-between border-b border-border py-1.5 text-sm">
                      <span>{b.branch_name}</span><span className="text-muted-foreground">{b.visits} visit(s) · {rupee(b.spend)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <p className="font-medium mb-2">Billing History</p>
                {detail.invoices?.length ? detail.invoices.map((i) => (
                  <div key={i.id} className="flex justify-between border-b border-border py-1.5">
                    <span>{i.invoice_no}</span><span className="text-muted-foreground">{i.bill_date}</span><span className="text-primary">{rupee(i.grand_total)}</span>
                  </div>
                )) : <p className="text-muted-foreground">No bills yet.</p>}
              </div>
              <div className="rounded-md border border-border p-3" data-testid="cust-marketing-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium flex items-center gap-1.5"><MessageSquare size={15} className="text-primary" /> WhatsApp Marketing</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${marketing?.marketing_opted_in ? "text-green-400" : "text-red-400"}`} data-testid="cust-consent-status">{marketing?.marketing_opted_in ? "OPTED IN" : "OPTED OUT"}</span>
                    {isAdmin && <Switch checked={!!marketing?.marketing_opted_in} onCheckedChange={toggleConsent} data-testid="cust-consent-toggle" />}
                  </div>
                </div>
                {marketing?.history?.length ? (
                  <div className="space-y-1 max-h-52 overflow-y-auto" data-testid="cust-marketing-history">
                    {marketing.history.map((h, i) => (
                      <div key={i} className="flex items-center justify-between border-b border-border/50 py-1.5 text-xs gap-2">
                        <div className="min-w-0"><p className="truncate text-foreground">{h.campaign}</p><p className="text-muted-foreground">{(h.sent_date || "").slice(0, 10)}</p></div>
                        <div className="flex gap-1.5 shrink-0">
                          {h.failed ? <span className="text-red-400">Failed</span>
                            : <><span className={h.delivered ? "text-green-400" : "text-muted-foreground"}>Delivered</span>
                                <span className={h.read ? "text-blue-400" : "text-muted-foreground"}>Read</span>
                                {h.replied && <span className="text-primary">Replied</span>}</>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-muted-foreground">No marketing messages sent to this customer yet.</p>}
              </div>
              {detail.referral_code && (
                <div className="rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="cust-referral">
                  <p className="text-xs text-muted-foreground">My Referral Code</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-lg text-primary" data-testid="cust-referral-code">{detail.referral_code}</span>
                    <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => { navigator.clipboard.writeText(detail.referral_code); toast.success("Copied"); }} data-testid="cust-referral-copy">Copy</Button>
                  </div>
                  <div className="flex gap-6 mt-2 text-xs text-muted-foreground">
                    <span>Successful Referrals: <span className="text-foreground">{detail.successful_referrals || 0}</span></span>
                    <span>Cashback Earned: <span className="text-foreground">{rupee(detail.total_referral_cashback || 0)}</span></span>
                  </div>
                </div>
              )}
              {detail.referrals?.length > 0 && (
                <div data-testid="cust-referral-history">
                  <p className="font-medium mb-2">Referral History</p>
                  {detail.referrals.map((r) => (
                    <div key={r.id} className="flex justify-between border-b border-border py-1.5 text-xs">
                      <span>{r.referred_customer_name || r.referred_mobile}</span>
                      <span className="text-muted-foreground">{r.invoice_no}</span>
                      <span className="text-green-300">+{rupee(r.cashback_amount)}</span>
                      <span className={r.reversed ? "text-orange-300" : "text-muted-foreground"}>{r.reversed ? "Reversed" : "Completed"}</span>
                    </div>
                  ))}
                </div>
              )}
                <div data-testid="cust-wallet-ledger">
                  <p className="font-medium mb-2">Wallet Ledger (all branches)</p>
                  {detail.wallet_ledger.map((w) => (
                    <div key={w.id} className="flex justify-between border-b border-border py-1.5 text-xs">
                      <span className="capitalize">{(w.kind || "").replace(/_/g, " ")}</span>
                      <span className="text-muted-foreground">{(w.created_at || "").slice(0, 10)}</span>
                      <span className={w.delta >= 0 ? "text-green-300" : "text-orange-300"}>{w.delta >= 0 ? "+" : ""}{rupee(w.delta)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={load} branches={branches} />
    </div>
  );
}

const IMPORT_FIELDS = [["name", "Customer Name"], ["mobile", "Mobile Number"], ["gender", "Gender"], ["email", "Email"], ["dob", "Date of Birth"], ["anniversary", "Anniversary"], ["branch", "Branch"], ["last_visit", "Last Visit Date"], ["total_visits", "Number of Visits"], ["total_spending", "Total Spending"], ["notes", "Notes"]];

function ImportDialog({ open, onClose, onDone, branches }) {
  const [step, setStep] = useState(1);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [branchMode, setBranchMode] = useState("all");
  const [branchId, setBranchId] = useState("");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(1); setParsed(null); setMapping({}); setBranchMode("all"); setBranchId(""); setSummary(null); };
  const close = () => { reset(); onClose(); };

  const onFile = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/customers/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data); setMapping(data.suggested || {}); setStep(2);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/customers/import/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = "customer_import_template.xlsx"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const analyze = async () => {
    if (!mapping.mobile) return toast.error("Map the Mobile Number column (required)");
    setBusy(true);
    try {
      const { data } = await api.post("/customers/import/analyze", { rows: parsed.rows, mapping, branch_mode: branchMode, branch_id: branchId || null });
      setSummary(data.summary); setStep(3);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const commit = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/customers/import/commit", { rows: parsed.rows, mapping, branch_mode: branchMode, branch_id: branchId || null, mode });
      toast.success(`Imported ${data.imported_new} new${data.updated_existing ? `, updated ${data.updated_existing}` : ""}${data.skipped_existing ? `, skipped ${data.skipped_existing}` : ""}`);
      close(); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const shownFields = IMPORT_FIELDS.filter(([f]) => mapping[f]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Import Customers</DialogTitle></DialogHeader>

        {step === 1 && (
          <div className="space-y-4" data-testid="import-step1">
            <p className="text-sm text-muted-foreground">Upload an Excel (.xlsx/.xls), CSV or table-based PDF. A valid mobile number is required per customer (used to prevent duplicates across all branches).</p>
            <label className="border-2 border-dashed border-border rounded-lg p-8 flex flex-col items-center gap-2 cursor-pointer hover:border-primary transition-colors">
              <FileUp size={28} className="text-primary" />
              <span className="text-sm">{busy ? "Reading file…" : "Click to choose a file"}</span>
              <input type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden" data-testid="import-file" onChange={(e) => onFile(e.target.files[0])} />
            </label>
            <Button variant="outline" className="rounded-sm" onClick={downloadTemplate} data-testid="import-template"><FileSpreadsheet size={15} className="mr-1" /> Download Sample Excel Template</Button>
          </div>
        )}

        {step === 2 && parsed && (
          <div className="space-y-4" data-testid="import-step2">
            <p className="text-sm">Found <span className="text-primary">{parsed.total}</span> rows. Map your columns to customer fields:</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {IMPORT_FIELDS.map(([f, label]) => (
                <div key={f} className="flex items-center gap-2">
                  <Label className="text-xs w-32 shrink-0">{label}{f === "mobile" ? " *" : ""}</Label>
                  <Select value={mapping[f] || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, [f]: v === "none" ? undefined : v }))}>
                    <SelectTrigger className="bg-secondary h-8 text-xs" data-testid={`import-map-${f}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{parsed.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="border border-border rounded p-3 space-y-2">
              <Label className="text-xs">Branch assignment</Label>
              <div className="flex flex-wrap gap-3 text-xs">
                <label className="flex items-center gap-1 cursor-pointer"><input type="radio" checked={branchMode === "all"} onChange={() => setBranchMode("all")} data-testid="import-branch-all" /> All-branch (central)</label>
                <label className="flex items-center gap-1 cursor-pointer"><input type="radio" checked={branchMode === "column"} onChange={() => setBranchMode("column")} data-testid="import-branch-column" /> From Branch column</label>
                <label className="flex items-center gap-1 cursor-pointer"><input type="radio" checked={branchMode === "fixed"} onChange={() => setBranchMode("fixed")} data-testid="import-branch-fixed" /> Assign to one branch</label>
              </div>
              {branchMode === "fixed" && (
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger className="bg-secondary h-8 text-xs w-64" data-testid="import-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="overflow-x-auto border border-border rounded">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-border">{shownFields.map(([f, l]) => <th key={f} className="px-2 py-1 text-left text-muted-foreground">{l}</th>)}</tr></thead>
                <tbody>{(parsed.rows || []).slice(0, 8).map((r, i) => <tr key={i} className="border-b border-border/50">{shownFields.map(([f]) => <td key={f} className="px-2 py-1">{r[mapping[f]] || ""}</td>)}</tr>)}</tbody>
              </table>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={analyze} disabled={busy || !mapping.mobile} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="import-analyze">Analyze</Button>
            </div>
          </div>
        )}

        {step === 3 && summary && (
          <div className="space-y-4" data-testid="import-step3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center text-sm">
              <div className="bg-secondary rounded p-3"><p className="font-display text-2xl" data-testid="sum-total">{summary.total}</p><p className="text-xs text-muted-foreground">Total rows</p></div>
              <div className="bg-secondary rounded p-3"><p className="font-display text-2xl text-green-300" data-testid="sum-new">{summary.new}</p><p className="text-xs text-muted-foreground">New customers</p></div>
              <div className="bg-secondary rounded p-3"><p className="font-display text-2xl text-blue-300" data-testid="sum-existing">{summary.existing}</p><p className="text-xs text-muted-foreground">Existing / duplicates</p></div>
              <div className="bg-secondary rounded p-3"><p className="font-display text-2xl text-red-300" data-testid="sum-invalid">{summary.invalid}</p><p className="text-xs text-muted-foreground">Invalid mobile</p></div>
              <div className="bg-secondary rounded p-3"><p className="font-display text-2xl text-orange-300" data-testid="sum-missing">{summary.missing_required}</p><p className="text-xs text-muted-foreground">Missing required</p></div>
            </div>
            <p className="text-xs text-muted-foreground">Existing customer history, wallet, billing and appointments are never overwritten. "Update Existing" only fills in the fields present in your file.</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <Button variant="ghost" onClick={() => setStep(2)}>Back</Button>
              <Button variant="outline" className="rounded-sm" onClick={() => commit("new_only")} disabled={busy} data-testid="import-new-only">Import New Only</Button>
              <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" onClick={() => commit("update_new")} disabled={busy} data-testid="import-update-new">Update Existing + Import New</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
